import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell,
  MessageCircle,
  Receipt,
  HelpCircle,
  Check,
  Clock,
  CheckCircle2,
  XCircle,
  Volume2,
  VolumeX,
  History,
} from 'lucide-react';
import {
  subscribeToWaiterRequests,
  subscribeToWaiterRequestsForWaiter,
  acknowledgeRequest,
  resolveRequest,
  getRequestHistory,
} from '../services/waiterRequestsService';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { staffAdvancedService } from '../services/staffAdvancedService';
import toast from 'react-hot-toast';
import { useThemeStore } from '../store/useThemeStore';

// ============================================================================
// CONSTANTS
// ============================================================================

// `color`: tono vivo para iconos y fondos tintados (sirve en ambos temas).
// `colorText`: variante oscurecida para TEXTO sobre el panel claro (legibilidad).
const TYPE_CONFIG = {
  assistance: { label: 'Asistencia', icon: MessageCircle, color: '#3498DB', colorText: '#1D6FA5' },
  bill_request: { label: 'Pedir Cuenta', icon: Receipt, color: '#7AC943', colorText: '#4F8A1F' },
  question: { label: 'Pregunta', icon: HelpCircle, color: '#9B59B6', colorText: '#7E3F9E' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: '#F59E0B', colorText: '#B45309', icon: Clock },
  acknowledged: { label: 'En atención', color: '#3498DB', colorText: '#1D6FA5', icon: CheckCircle2 },
  resolved: { label: 'Resuelto', color: '#22C55E', colorText: '#15803D', icon: Check },
  cancelled: { label: 'Cancelado', color: '#6B7280', colorText: '#4B5563', icon: XCircle },
};

// Etiquetas de tipo de ausencia para las solicitudes de turno del staff.
const ABSENCE_LABELS = { vacation: 'Vacaciones', day_off: 'Día libre', permit: 'Permiso' };

const ALERT_SOUND_SRC = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Dg4F9eXZ7goiIg3x0b3F7hIuKhX14dHR5goqMiYN7dHN1e4WNj4mCeXNydXyGjo+Ig3l0c3V8h5CQiIN5c3N1fIeRkYiDeHNzdXyIkpKIg3hzc3V8iJKSiIN4c3N1fIiSkoiDeHNzdXyIkpKIg3hzc3V8iJKSiIN4c3N0dXuGj4+Ig3l0c3V8h5CQiIN5c3N1fIeRkYiDeHRzdXyHkZGIg3hzc3R7hpCQiIN5dHN1fIeQkIiDeXRzdXyHkJCIg3l0c3V8hpCQiIR5dHR1fIaQj4iDeXR0dXyGkI+Ig3l0dHV8hpCPiIR5dHR1e4aPj4iEenR0dXuGj4+IhHp0dHV7hY+PiIR6dHR1e4WOjoiEenR1dXuFjo6IhHp1dXV7hY6OiIR6dXV1e4WOjoiEenV1dXuFjo6IhHp1dXV7hY6OiIR6dXV1e4WOjg==';

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ============================================================================
// NOTIFICATION DROPDOWN
// ============================================================================

/** Rango [medianoche, +24h) de HOY en TZ Chile (patrón TableManagement). */
function todayChileRange() {
  const dateStr = new Date().toLocaleString('en-CA', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const start = new Date(`${dateStr}T00:00:00.000-04:00`);
  return {
    start: Timestamp.fromDate(start),
    end: Timestamp.fromDate(new Date(start.getTime() + 24 * 3600 * 1000)),
  };
}

const NotificationDropdown = ({ restaurantId, staffName, viewerUid, canViewAll = false, canManageArrivals = false }) => {
  const isDark = useThemeStore((s) => s.theme === 'dark');
  // Texto coloreado: tono vivo en oscuro, variante oscurecida en claro.
  const textColor = (cfg) => (isDark ? cfg.color : (cfg.colorText || cfg.color));
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [activeRequests, setActiveRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevCountRef = useRef(0);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  // Confirmación de llegada (solo con permiso staffAmplifier)
  const [arrivals, setArrivals] = useState([]);
  const [resolvingArrival, setResolvingArrival] = useState(null);
  const [rejectingArrival, setRejectingArrival] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const prevArrivalsRef = useRef(0);
  // Solicitudes de turno/ausencia pendientes (mismo permiso staffAmplifier).
  const [shiftReqs, setShiftReqs] = useState([]);
  const [resolvingReq, setResolvingReq] = useState(null);
  const [rejectingReq, setRejectingReq] = useState(null);
  const [reqRejectNote, setReqRejectNote] = useState('');
  const prevReqsRef = useRef(0);

  // Subscribe to active requests.
  // - admin/owner (canViewAll) → todas las solicitudes del restaurante;
  // - garzón/cajero/host → solo las de sus mesas asignadas (assignedWaiterId == uid).
  useEffect(() => {
    if (!restaurantId) return;
    if (!canViewAll && !viewerUid) {
      // Staff sin uid resuelto todavía: no mostrar nada hasta tenerlo (evita fuga).
      setActiveRequests([]);
      return;
    }
    const onRequests = (requests) => {
      setActiveRequests(requests);

      // Play sound on new pending request
      if (soundEnabled && requests.length > prevCountRef.current) {
        try {
          if (!audioRef.current) audioRef.current = new Audio(ALERT_SOUND_SRC);
          audioRef.current.play().catch(() => {});
        } catch (_) { /* audio not critical */ }
      }
      prevCountRef.current = requests.length;
    };
    const unsubscribe = canViewAll
      ? subscribeToWaiterRequests(restaurantId, onRequests)
      : subscribeToWaiterRequestsForWaiter(restaurantId, viewerUid, onRequests);
    return () => unsubscribe();
  }, [restaurantId, soundEnabled, canViewAll, viewerUid]);

  // Llegadas por confirmar (features.clockInConfirmation): shiftRecords de HOY
  // con solicitud pendiente. Solo se suscribe con permiso staffAmplifier — el
  // callable resolveClockInRequest exige ese permiso (no el rol admin).
  useEffect(() => {
    if (!restaurantId || !canManageArrivals) {
      setArrivals([]);
      return undefined;
    }
    const { start, end } = todayChileRange();
    const q = query(
      collection(db, 'restaurants', restaurantId, 'shiftRecords'),
      where('clockInRequestStatus', '==', 'pending'),
      where('date', '>=', start),
      where('date', '<', end)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      // Solo accionables: el cron de medianoche puede dejar solicitudes
      // residuales en records ya completados.
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.status === 'scheduled' && !r.clockInAt)
        .sort((a, b) => (a.clockInRequestedAt?.seconds || 0) - (b.clockInRequestedAt?.seconds || 0));
      setArrivals(rows);
      if (soundEnabled && rows.length > prevArrivalsRef.current) {
        try {
          if (!audioRef.current) audioRef.current = new Audio(ALERT_SOUND_SRC);
          audioRef.current.play().catch(() => {});
        } catch (_) { /* no crítico */ }
      }
      prevArrivalsRef.current = rows.length;
    }, (err) => console.error('[NotificationDropdown] llegadas:', err));
    return () => unsubscribe();
  }, [restaurantId, canManageArrivals, soundEnabled]);

  // Solicitudes de turno/ausencia PENDIENTES en tiempo real. Mismo permiso que
  // las llegadas: resolveShiftRequest exige staffAmplifier. Suena la alerta al
  // llegar una nueva, para que el encargado sepa que debe aprobar/rechazar.
  useEffect(() => {
    if (!restaurantId || !canManageArrivals) {
      setShiftReqs([]);
      return undefined;
    }
    const q = query(
      collection(db, 'restaurants', restaurantId, 'shiftRequests'),
      where('status', '==', 'pending')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setShiftReqs(rows);
      if (soundEnabled && rows.length > prevReqsRef.current) {
        try {
          if (!audioRef.current) audioRef.current = new Audio(ALERT_SOUND_SRC);
          audioRef.current.play().catch(() => {});
        } catch (_) { /* no crítico */ }
      }
      prevReqsRef.current = rows.length;
    }, (err) => console.error('[NotificationDropdown] solicitudes de turno:', err));
    return () => unsubscribe();
  }, [restaurantId, canManageArrivals, soundEnabled]);

  const handleResolveRequest = useCallback(async (action, requestId, resolutionNote) => {
    setResolvingReq(requestId);
    try {
      await staffAdvancedService.resolveShiftRequest({
        restaurantId, requestId, action, resolutionNote: resolutionNote || null,
      });
      toast.success(action === 'approve' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setRejectingReq(null);
      setReqRejectNote('');
    } catch (err) {
      console.error('Error resolviendo solicitud de turno:', err);
      toast.error(err?.message || 'No se pudo resolver la solicitud');
    } finally {
      setResolvingReq(null);
    }
  }, [restaurantId]);

  const handleResolveArrival = useCallback(async (decision, shiftRecordId, reason) => {
    setResolvingArrival(shiftRecordId);
    try {
      await staffAdvancedService.resolveClockInRequest({ restaurantId, shiftRecordId, decision, reason });
      toast.success(decision === 'confirm' ? 'Llegada confirmada: turno iniciado' : 'Llegada rechazada');
      setRejectingArrival(null);
      setRejectReason('');
    } catch (err) {
      console.error('Error resolviendo llegada:', err);
      toast.error(err?.message || 'No se pudo resolver la solicitud');
    } finally {
      setResolvingArrival(null);
    }
  }, [restaurantId]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Load history on tab switch (mismo alcance que la suscripción activa)
  useEffect(() => {
    if (activeTab === 'history' && isOpen && restaurantId) {
      if (!canViewAll && !viewerUid) {
        setHistory([]);
        return;
      }
      setLoadingHistory(true);
      getRequestHistory(restaurantId, 20, canViewAll ? null : viewerUid)
        .then(setHistory)
        .catch(console.error)
        .finally(() => setLoadingHistory(false));
    }
  }, [activeTab, isOpen, restaurantId, canViewAll, viewerUid]);

  const handleAcknowledge = useCallback(async (requestId) => {
    try {
      await acknowledgeRequest(restaurantId, requestId, staffName);
    } catch (err) {
      console.error('Error acknowledging request:', err);
    }
  }, [restaurantId, staffName]);

  const handleResolve = useCallback(async (requestId) => {
    try {
      await resolveRequest(restaurantId, requestId);
    } catch (err) {
      console.error('Error resolving request:', err);
    }
  }, [restaurantId]);

  const pendingCount = activeRequests.filter((r) => r.status === 'pending').length;
  const totalActiveCount = activeRequests.length;
  const badgeCount = totalActiveCount + arrivals.length + shiftReqs.length;

  const renderArrival = (shift) => (
    <div
      key={shift.id}
      className="px-3 py-2.5 hover:bg-surface-3/50 transition-colors border-b border-line-subtle last:border-0"
    >
      <div className="flex items-start gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: '#F59E0B20' }}
        >
          <Clock size={16} style={{ color: '#F59E0B' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-content-primary text-sm font-medium truncate">
              {shift.staffName || 'Staff'}
            </span>
            <span className="text-content-subtle text-xs shrink-0">
              {timeAgo(shift.clockInRequestedAt)}
            </span>
          </div>
          <p className="text-content-subtle text-xs mt-0.5">
            Avisó su llegada{shift.shiftTemplateName ? ` · ${shift.shiftTemplateName}` : ''}
            {shift.shiftStart ? ` (${shift.shiftStart}–${shift.shiftEnd || '?'})` : ''}
          </p>
        </div>
      </div>
      {rejectingArrival === shift.id ? (
        <div className="flex items-center gap-1.5 mt-2 ml-10">
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Motivo (opcional)"
            className="flex-1 px-2 py-1.5 bg-surface-3 border border-line rounded-lg text-xs text-content-primary"
          />
          <button
            onClick={() => handleResolveArrival('reject', shift.id, rejectReason)}
            disabled={resolvingArrival === shift.id}
            className="px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
            style={{ backgroundColor: '#EF444420', color: isDark ? '#EF4444' : '#B91C1C', border: '1px solid #EF444430' }}
          >
            Rechazar
          </button>
          <button
            onClick={() => { setRejectingArrival(null); setRejectReason(''); }}
            className="px-1.5 py-1.5 text-content-subtle hover:text-content-primary text-xs"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="flex gap-2 mt-2 ml-10">
          <button
            onClick={() => handleResolveArrival('confirm', shift.id)}
            disabled={resolvingArrival === shift.id}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
            style={{ backgroundColor: '#22C55E20', color: isDark ? '#22C55E' : '#15803D', border: '1px solid #22C55E30' }}
          >
            <CheckCircle2 size={12} />
            Confirmar llegada
          </button>
          <button
            onClick={() => { setRejectingArrival(shift.id); setRejectReason(''); }}
            disabled={resolvingArrival === shift.id}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
            style={{ backgroundColor: '#EF444420', color: isDark ? '#EF4444' : '#B91C1C', border: '1px solid #EF444430' }}
          >
            <XCircle size={12} />
            Rechazar
          </button>
        </div>
      )}
    </div>
  );

  const renderShiftRequest = (req) => {
    const isShift = req.type === 'shift';
    const detail = isShift
      ? `Pide turno${req.shiftTemplateName ? `: ${req.shiftTemplateName}` : ''}${req.dateStr ? ` · ${req.dateStr}` : ''}`
      : `Pide ${(ABSENCE_LABELS[req.absenceType] || 'ausencia').toLowerCase()}${
          req.startDateStr
            ? ` · ${req.startDateStr}${req.endDateStr && req.endDateStr !== req.startDateStr ? ` al ${req.endDateStr}` : ''}`
            : ''
        }${req.reason ? ` · ${req.reason}` : ''}`;
    return (
      <div
        key={req.id}
        className="px-3 py-2.5 hover:bg-surface-3/50 transition-colors border-b border-line-subtle last:border-0"
      >
        <div className="flex items-start gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: '#8B5CF620' }}
          >
            <Clock size={16} style={{ color: '#8B5CF6' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-content-primary text-sm font-medium truncate">
                {req.staffName || 'Staff'}
              </span>
              <span className="text-content-subtle text-xs shrink-0">{timeAgo(req.createdAt)}</span>
            </div>
            <p className="text-content-subtle text-xs mt-0.5">{detail}</p>
          </div>
        </div>
        {rejectingReq === req.id ? (
          <div className="flex items-center gap-1.5 mt-2 ml-10">
            <input
              type="text"
              value={reqRejectNote}
              onChange={(e) => setReqRejectNote(e.target.value)}
              placeholder="Nota de rechazo (opcional)"
              className="flex-1 px-2 py-1.5 bg-surface-3 border border-line rounded-lg text-xs text-content-primary"
            />
            <button
              onClick={() => handleResolveRequest('reject', req.id, reqRejectNote)}
              disabled={resolvingReq === req.id}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
              style={{ backgroundColor: '#EF444420', color: isDark ? '#EF4444' : '#B91C1C', border: '1px solid #EF444430' }}
            >
              Rechazar
            </button>
            <button
              onClick={() => { setRejectingReq(null); setReqRejectNote(''); }}
              className="px-1.5 py-1.5 text-content-subtle hover:text-content-primary text-xs"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex gap-2 mt-2 ml-10">
            <button
              onClick={() => handleResolveRequest('approve', req.id)}
              disabled={resolvingReq === req.id}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
              style={{ backgroundColor: '#22C55E20', color: isDark ? '#22C55E' : '#15803D', border: '1px solid #22C55E30' }}
            >
              <CheckCircle2 size={12} />
              Aprobar
            </button>
            <button
              onClick={() => { setRejectingReq(req.id); setReqRejectNote(''); }}
              disabled={resolvingReq === req.id}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
              style={{ backgroundColor: '#EF444420', color: isDark ? '#EF4444' : '#B91C1C', border: '1px solid #EF444430' }}
            >
              <XCircle size={12} />
              Rechazar
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderRequest = (req, showActions = true) => {
    const typeInfo = TYPE_CONFIG[req.type] || TYPE_CONFIG.assistance;
    const statusInfo = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
    const TypeIcon = typeInfo.icon;
    const StatusIcon = statusInfo.icon;

    return (
      <div
        key={req.id}
        className="px-3 py-2.5 hover:bg-surface-3/50 transition-colors border-b border-line-subtle last:border-0"
      >
        <div className="flex items-start gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: typeInfo.color + '20' }}
          >
            <TypeIcon size={16} style={{ color: typeInfo.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-content-primary text-sm font-medium truncate">
                {req.tableName || 'Mesa'}
              </span>
              <span className="text-content-subtle text-xs shrink-0">
                {timeAgo(req.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <StatusIcon size={12} style={{ color: statusInfo.color }} />
              <span className="text-xs" style={{ color: textColor(statusInfo) }}>
                {typeInfo.label}
              </span>
            </div>
            {canViewAll && req.assignedWaiterName && (
              <p className="text-content-subtle text-xs mt-0.5">
                Garzón: {req.assignedWaiterName}
              </p>
            )}
            {canViewAll && !req.assignedWaiterId && (
              <p className="text-content-subtle text-xs mt-0.5 italic">
                Mesa sin garzón asignado
              </p>
            )}
            {req.message && (
              <p className="text-content-muted text-xs mt-1 line-clamp-2">
                "{req.message}"
              </p>
            )}
            {req.acknowledgedBy && req.status !== 'pending' && (
              <p className="text-content-subtle text-xs mt-0.5">
                Atendido por {req.acknowledgedBy}
              </p>
            )}
          </div>
        </div>

        {showActions && req.status === 'pending' && (
          <div className="flex gap-2 mt-2 ml-10">
            <button
              onClick={() => handleAcknowledge(req.id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: '#3498DB20',
                color: isDark ? '#3498DB' : '#1D6FA5',
                border: '1px solid #3498DB30',
              }}
            >
              <CheckCircle2 size={12} />
              Atender
            </button>
            <button
              onClick={() => handleResolve(req.id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: '#22C55E20',
                color: isDark ? '#22C55E' : '#15803D',
                border: '1px solid #22C55E30',
              }}
            >
              <Check size={12} />
              Resuelto
            </button>
          </div>
        )}

        {showActions && req.status === 'acknowledged' && (
          <div className="flex gap-2 mt-2 ml-10">
            <button
              onClick={() => handleResolve(req.id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: '#22C55E20',
                color: isDark ? '#22C55E' : '#15803D',
                border: '1px solid #22C55E30',
              }}
            >
              <Check size={12} />
              Marcar Resuelto
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative" style={{ zIndex: isOpen ? 99999 : undefined }} ref={dropdownRef}>
      {/* Bell trigger */}
      <button
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell size={20} />
        {badgeCount > 0 && (
          <span className="notification-badge">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl overflow-hidden"
          style={{
            zIndex: 99999,
            backgroundColor: 'rgb(var(--surface-2))',
            border: '1px solid rgb(var(--line-strong) / 0.5)',
          }}
        >
          {/* Header */}
          <div className="px-3 py-2.5 flex items-center justify-between border-b border-line-subtle">
            <div className="flex items-center gap-2">
              <span className="text-content-primary text-sm font-semibold">
                Solicitudes
              </span>
              {pendingCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded text-xs font-bold"
                  style={{ backgroundColor: '#F59E0B20', color: isDark ? '#F59E0B' : '#B45309' }}
                >
                  {pendingCount} nuevas
                </span>
              )}
            </div>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-1 rounded hover:bg-surface-3 transition-colors text-content-subtle hover:text-content-secondary"
              title={soundEnabled ? 'Silenciar alertas' : 'Activar alertas'}
            >
              {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-line-subtle">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'text-content-primary border-b-2 border-brand'
                  : 'text-content-subtle hover:text-content-secondary'
              }`}
            >
              Activas ({totalActiveCount})
            </button>
            {canManageArrivals && (
              <button
                onClick={() => setActiveTab('arrivals')}
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  activeTab === 'arrivals'
                    ? 'text-content-primary border-b-2 border-brand'
                    : 'text-content-subtle hover:text-content-secondary'
                }`}
              >
                Llegadas ({arrivals.length})
              </button>
            )}
            {canManageArrivals && (
              <button
                onClick={() => setActiveTab('shiftreqs')}
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  activeTab === 'shiftreqs'
                    ? 'text-content-primary border-b-2 border-brand'
                    : 'text-content-subtle hover:text-content-secondary'
                }`}
              >
                Turnos ({shiftReqs.length})
              </button>
            )}
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                activeTab === 'history'
                  ? 'text-content-primary border-b-2 border-brand'
                  : 'text-content-subtle hover:text-content-secondary'
              }`}
            >
              <History size={12} />
              Historial
            </button>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {activeTab === 'arrivals' && canManageArrivals ? (
              arrivals.length > 0 ? (
                arrivals.map(renderArrival)
              ) : (
                <div className="py-8 text-center">
                  <Clock size={24} className="mx-auto text-content-disabled mb-2" />
                  <p className="text-content-subtle text-sm">Sin llegadas por confirmar</p>
                  <p className="text-content-disabled text-xs mt-1">
                    Cuando alguien avise su llegada, aparecerá aquí
                  </p>
                </div>
              )
            ) : activeTab === 'shiftreqs' && canManageArrivals ? (
              shiftReqs.length > 0 ? (
                shiftReqs.map(renderShiftRequest)
              ) : (
                <div className="py-8 text-center">
                  <Clock size={24} className="mx-auto text-content-disabled mb-2" />
                  <p className="text-content-subtle text-sm">Sin solicitudes de turno</p>
                  <p className="text-content-disabled text-xs mt-1">
                    Cuando alguien pida un turno o avise una ausencia, aparecerá aquí
                  </p>
                </div>
              )
            ) : activeTab === 'pending' ? (
              activeRequests.length > 0 ? (
                activeRequests.map((req) => renderRequest(req, true))
              ) : (
                <div className="py-8 text-center">
                  <Bell size={24} className="mx-auto text-content-disabled mb-2" />
                  <p className="text-content-subtle text-sm">Sin solicitudes activas</p>
                  <p className="text-content-disabled text-xs mt-1">
                    Las solicitudes de los clientes aparecerán aquí
                  </p>
                </div>
              )
            ) : loadingHistory ? (
              <div className="py-8 text-center">
                <div className="w-6 h-6 border-2 border-line-active border-t-content-secondary rounded-full animate-spin mx-auto" />
              </div>
            ) : history.length > 0 ? (
              history.map((req) => renderRequest(req, false))
            ) : (
              <div className="py-8 text-center">
                <History size={24} className="mx-auto text-content-disabled mb-2" />
                <p className="text-content-subtle text-sm">Sin historial</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
